// src/store/reservationsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
   CALENDAR_DELETE_TOMBSTONE_TTL_MS,
   CALENDAR_LOCAL_ECHO_TTL_MS,
} from "../calendar/sync/tombstones";
import {
   getReservations,
   createReservations,
   getAllReservations,
   getUserReservations,
   patchReservation,
   deleteReservation,
   getBusyReservations,
   // meta endpoint (ETag / updated_since)
   getReservationsMeta,
   // NOU: filtrare + range pe lună
   filterReservationsAllPages,
   buildMonthRange,
} from "../api/reservationsService";
import { buildMonthSnapshotFromQueryPayload } from "../calendar/data/reservationsMonthBridge";
import { resolveServerTimeFromPayload } from "../calendar/master/sync/serverTime";

function resolveLastSyncedAt(payload) {
   return (
      resolveServerTimeFromPayload(payload) || new Date().toISOString()
   );
}

function reservationActionLooksDeleted(row) {
   if (!row || typeof row !== "object") return false;
   const actionRaw =
      row.action ??
      row.status ??
      row.type ??
      row.reservation?.action ??
      row.reservation?.status ??
      row.reservation?.type ??
      "";
   return /(delete|deleted|remove|removed)/i.test(String(actionRaw || ""));
}

function isReservationSoftDeleted(row) {
   if (!row || typeof row !== "object") return false;
   if (reservationActionLooksDeleted(row)) return true;

   return !!(
      row.isDeleted ??
      row.is_deleted ??
      row.deletedAt ??
      row.deleted_at ??
      row.reservation?.isDeleted ??
      row.reservation?.is_deleted ??
      row.reservation?.deletedAt ??
      row.reservation?.deleted_at
   );
}

function normalizeIncomingReservations(items) {
   if (!Array.isArray(items) || !items.length) return [];
   return items.filter((row) => !isReservationSoftDeleted(row));
}

function extractRawItemsFromResponse(res) {
   if (Array.isArray(res)) return res;
   if (!res || typeof res !== "object") return [];

   const candidates = [
      res.items,
      res.data,
      res.results,
      res.rows,
      res.reservations,
      res.list,
   ];

   for (const c of candidates) {
      if (Array.isArray(c)) return c;
      if (c && typeof c === "object") {
         if (Array.isArray(c.items)) return c.items;
         if (Array.isArray(c.data)) return c.data;
         if (Array.isArray(c.results)) return c.results;
         if (Array.isArray(c.rows)) return c.rows;
         if (Array.isArray(c.reservations)) return c.reservations;
         if (Array.isArray(c.list)) return c.list;
      }
   }

   return [];
}

function normalizeItemsFromResponse(res) {
   return normalizeIncomingReservations(extractRawItemsFromResponse(res));
}

function reservationEntityId(row) {
   if (!row || typeof row !== "object") return null;
   return (
      row.id ??
      row._id ??
      row.reservationId ??
      row.reservation_id ??
      row.reservation?.id ??
      null
   );
}

function reservationEntityIdStr(row) {
   const id = reservationEntityId(row);
   if (id == null) return "";
   return String(id).trim();
}

function normalizeReservationIdLike(value) {
   if (value == null) return "";
   return String(value).trim();
}

const LOCAL_ECHO_TTL_MS = CALENDAR_LOCAL_ECHO_TTL_MS;
// Cât timp ținem o „piatră de mormânt" locală pentru un rând șters optimist,
// ca un refetch/delta întârziat de la server (replica lag) să nu-l reînvie
// (simptom la mutări rapide: cardul mutat reapare la locul vechi).
const LOCAL_DELETE_TOMBSTONE_TTL_MS = CALENDAR_DELETE_TOMBSTONE_TTL_MS;

function pruneRecentlyDeleted(map, now = Date.now()) {
   if (!map || typeof map !== "object") return;
   for (const key of Object.keys(map)) {
      const until = Number(map[key]);
      if (!Number.isFinite(until) || until <= now) delete map[key];
   }
}

function isRecentlyDeletedId(map, id, now = Date.now()) {
   if (!map || typeof map !== "object") return false;
   const key = normalizeReservationIdLike(id);
   if (!key) return false;
   const until = Number(map[key]);
   if (!Number.isFinite(until)) return false;
   if (until <= now) return false;
   return true;
}

function markRecentlyDeletedId(map, id, now = Date.now(), { extended = false } = {}) {
   if (!map || typeof map !== "object") return;
   const key = normalizeReservationIdLike(id);
   if (!key) return;
   const ttlMs = extended
      ? Math.max(LOCAL_DELETE_TOMBSTONE_TTL_MS, 5 * 60 * 1000)
      : LOCAL_DELETE_TOMBSTONE_TTL_MS;
   map[key] = now + ttlMs;
}

function clearRecentlyDeletedId(map, id) {
   if (!map || typeof map !== "object") return;
   const key = normalizeReservationIdLike(id);
   if (!key) return;
   if (Object.prototype.hasOwnProperty.call(map, key)) delete map[key];
}

/* ───────────────────── NEW: Delta sync (doar ce s-a schimbat) ───────────────────── */
let deltaFetchInFlight = null;

export const fetchReservationsDelta = createAsyncThunk(
   "reservations/fetchReservationsDelta",
   async (_, { getState, rejectWithValue }) => {
      if (deltaFetchInFlight) {
         return deltaFetchInFlight;
      }

      const runFetch = async () => {
         const st = getState().reservations || {};
         const since = st.lastSyncedAt || null;

         const opts = since
            ? { scope: "all", updated_since: since, pageSize: 5000 }
            : { scope: "all", pageSize: 5000 };

         const res = await getAllReservations(opts);

         // Acceptăm:
         // 1) { items: [...], deleted: [...], etag, serverTime }
         // 2) direct: [...]
         const payload = Array.isArray(res) ? { items: res } : res || {};

         const deletedRaw = Array.isArray(
            payload.deleted ??
               payload.deletedIds ??
               payload.removed ??
               payload.tombstones,
         )
            ? payload.deleted ??
              payload.deletedIds ??
              payload.removed ??
              payload.tombstones
            : [];
         const rawItems = extractRawItemsFromResponse(payload);
         const items = normalizeIncomingReservations(rawItems);
         const deletedFromItems = [];
         for (const row of rawItems) {
            if (!isReservationSoftDeleted(row)) continue;
            const id = reservationEntityIdStr(row);
            if (!id || deletedFromItems.includes(id)) continue;
            deletedFromItems.push(id);
         }
         const deleted = [];
         const deletedSeen = new Set();
         const pushDeletedId = (value) => {
            const id =
               normalizeReservationIdLike(value) ||
               normalizeReservationIdLike(reservationEntityId(value)) ||
               normalizeReservationIdLike(value?.id) ||
               normalizeReservationIdLike(value?.reservationId) ||
               normalizeReservationIdLike(value?.reservation_id);
            if (!id || deletedSeen.has(id)) return;
            deletedSeen.add(id);
            deleted.push(id);
         };
         for (const value of deletedRaw) pushDeletedId(value);
         for (const value of deletedFromItems) pushDeletedId(value);
         const etag = payload.etag || null;
         const now = payload.serverTime || new Date().toISOString();

         return { items, deleted, etag, now };
      };

      deltaFetchInFlight = runFetch()
         .catch((error) => {
            throw error;
         })
         .finally(() => {
            deltaFetchInFlight = null;
         });

      try {
         return await deltaFetchInFlight;
      } catch (e) {
         return rejectWithValue(e.message);
      }
   },
);

/* ───────────── NEW: Meta-check inteligent (polling smart) ─────────────
   - Întoarce { refreshed:boolean, reason:string, etag?:string, serverTime?:string }
   - Dacă serverul zice „nu s-a schimbat” → NU mai tragem delta
   - Dacă dă eroare → încercăm delta ca fallback sigur
------------------------------------------------------------------------ */
export const maybeRefreshReservations = createAsyncThunk(
   "reservations/maybeRefreshReservations",
   async (_, { getState, dispatch, rejectWithValue }) => {
      const st = getState().reservations || {};
      const updated_since = st.lastSyncedAt || undefined;
      const etag = st.etag || undefined;

      try {
         const meta = await getReservationsMeta({ updated_since, etag });
         // meta: { changed:boolean, etag?:string, serverTime?:string }
         if (meta?.changed) {
            await dispatch(fetchReservationsDelta());
            return {
               refreshed: true,
               reason: "meta-changed",
               etag: meta?.etag || null,
               serverTime: meta?.serverTime || null,
            };
         }
         return {
            refreshed: false,
            reason: "not-modified",
            etag: meta?.etag || etag || null,
            serverTime: meta?.serverTime || null,
         };
      } catch (err) {
         // fallback sigur: încearcă delta; dacă nici delta nu merge, raportează
         try {
            await dispatch(fetchReservationsDelta());
            return {
               refreshed: true,
               reason: "error-fallback",
               etag: null,
               serverTime: null,
            };
         } catch (e2) {
            return rejectWithValue(
               err?.message || "maybeRefreshReservations failed",
            );
         }
      }
   },
);

// --- Async thunks (ale tale) ---
export const fetchReservations = createAsyncThunk(
   "reservations/fetchReservations",
   async (_, { rejectWithValue }) => {
      try {
         return await getReservations();
      } catch (e) {
         return rejectWithValue(e.message);
      }
   },
);

export const fetchAllReservations = createAsyncThunk(
   "reservations/fetchAllReservations",
   async (_, { rejectWithValue }) => {
      try {
         return await getAllReservations();
      } catch (e) {
         return rejectWithValue(e.message);
      }
   },
);

export const fetchUserReservations = createAsyncThunk(
   "reservations/fetchUserReservations",
   async (userId, { rejectWithValue }) => {
      try {
         return await getUserReservations(userId);
      } catch (e) {
         return rejectWithValue(e.message);
      }
   },
);

export const addReservation = createAsyncThunk(
   "reservations/addReservation",
   async (payload, { rejectWithValue }) => {
      try {
         return await createReservations(payload);
      } catch (e) {
         return rejectWithValue(e.message);
      }
   },
);

export const updateReservation = createAsyncThunk(
   "reservations/updateReservation",
   async ({ id, data }, { rejectWithValue }) => {
      try {
         return await patchReservation(id, data);
      } catch (e) {
         return rejectWithValue(e.message);
      }
   },
);

export const updateReservationColor = createAsyncThunk(
   "reservations/updateReservationColor",
   async ({ id, color }, { rejectWithValue }) => {
      try {
         return await patchReservation(id, { color });
      } catch (e) {
         return rejectWithValue(e.message);
      }
   },
);

export const removeReservation = createAsyncThunk(
   "reservations/removeReservation",
   async (id, { rejectWithValue }) => {
      try {
         await deleteReservation(id);
         return id;
      } catch (e) {
         return rejectWithValue(e.message);
      }
   },
);

export const fetchBusy = createAsyncThunk(
   "reservations/fetchBusy",
   async (query, { rejectWithValue }) => {
      try {
         const data = await getBusyReservations(query);
         return { query, data };
      } catch (e) {
         return rejectWithValue(e.message);
      }
   },
);

/* ───────────── NEW: filtrare după /api/reservations/filter ───────────── */

/** Generic: orice combinație de filtre suportate de backend */
export const fetchReservationsFiltered = createAsyncThunk(
   "reservations/fetchReservationsFiltered",
   async (filters, { rejectWithValue }) => {
      try {
         const items = await filterReservationsAllPages(
            {
               ...(filters || {}),
               sortBy: filters?.sortBy ?? "startTime",
               sortOrder: filters?.sortOrder ?? "asc",
            },
            {
               pageSize: Math.max(1, Math.trunc(Number(filters?.limit) || 500)),
               maxItems: 15000,
            },
         );
         return { items, filters };
      } catch (e) {
         return rejectWithValue(e.message);
      }
   },
);

/** Concret: doar pe o lună (după o dată dată) */
export const fetchReservationsForMonth = createAsyncThunk(
   "reservations/fetchReservationsForMonth",
   async ({ date, extraFilters } = {}, { rejectWithValue }) => {
      try {
         const range = buildMonthRange(date);
         const items = await filterReservationsAllPages(
            {
               ...(extraFilters || {}),
               scope: "all",
               ...range,
            },
            {
               pageSize: 5000,
               maxItems: 15000,
               preferSinglePage: true,
               singlePageLimit: 12000,
            },
         );
         return { items, range };
      } catch (e) {
         return rejectWithValue(e.message);
      }
   },
);

// --- helpers interne (ale tale) ---
function getStart(r) {
   return (
      r?.startTime ??
      r?.start ??
      r?.start_time ??
      r?.dateTime ??
      r?.datetime ??
      r?.date ??
      r?.begin ??
      null
   );
}
function getGroupId(r) {
   return (
      r?.instructorsGroupId ??
      r?.groupId ??
      r?.group_id ??
      r?.instructors_group_id ??
      r?.group?.id ??
      null
   );
}
function getInstructorId(r) {
   return r?.instructorId ?? r?.instructor_id ?? r?.instructor?.id ?? null;
}
function getStudentId(r) {
   return r?.userId ?? r?.studentId ?? r?.user_id ?? r?.user?.id ?? null;
}

function removeReservationFromLoadedStudentCaches(state, reservationId) {
   const id = normalizeReservationIdLike(reservationId);
   if (!id) return;

   const buckets = state?.byStudent;
   if (!buckets || typeof buckets !== "object") return;

   for (const sid of Object.keys(buckets)) {
      const arr = buckets[sid];
      if (!Array.isArray(arr) || !arr.length) continue;
      const next = arr.filter((r) => reservationEntityIdStr(r) !== id);
      if (next.length !== arr.length) buckets[sid] = next;
   }
}

function findReservationInLoadedStudentCaches(state, reservationId) {
   const id = normalizeReservationIdLike(reservationId);
   if (!id) return null;

   const buckets = state?.byStudent;
   if (!buckets || typeof buckets !== "object") return null;

   for (const sid of Object.keys(buckets)) {
      const arr = buckets[sid];
      if (!Array.isArray(arr) || !arr.length) continue;
      const hit = arr.find((r) => reservationEntityIdStr(r) === id);
      if (hit) return hit;
   }

   return null;
}

const SUBSTITUTION_EMBED_KEYS = [
   "substitution",
   "instructorSubstitution",
   "effectiveInstructor",
   "effective_instructor",
];

function rowHasSubstitutionEmbed(row) {
   if (!row || typeof row !== "object") return false;

   for (const key of SUBSTITUTION_EMBED_KEYS) {
      const value = row[key];
      if (value != null && value !== false) return true;
   }

   const reservation = row.reservation;
   if (reservation && typeof reservation === "object") {
      for (const key of SUBSTITUTION_EMBED_KEYS) {
         const value = reservation[key];
         if (value != null && value !== false) return true;
      }
   }

   return false;
}

function reconcileSubstitutionEmbeds(merged, authorityRow) {
   if (!merged || typeof merged !== "object") return merged;
   if (!authorityRow || typeof authorityRow !== "object") return merged;
   if (rowHasSubstitutionEmbed(authorityRow)) return merged;

   const next = { ...merged };
   for (const key of SUBSTITUTION_EMBED_KEYS) {
      if (next[key] != null) next[key] = null;
   }

   if (next.reservation && typeof next.reservation === "object") {
      next.reservation = { ...next.reservation };
      for (const key of SUBSTITUTION_EMBED_KEYS) {
         if (next.reservation[key] != null) next.reservation[key] = null;
      }
   }

   return next;
}

function mergeReservationRows(...rows) {
   let merged = null;

   for (const row of rows) {
      if (!row || typeof row !== "object") continue;

      merged = merged ? { ...merged, ...row } : { ...row };

      if (merged?.reservation || row?.reservation) {
         merged.reservation = {
            ...(merged?.reservation || {}),
            ...(row?.reservation || {}),
         };
      }

      if (merged?.instructor || row?.instructor) {
         merged.instructor = {
            ...(merged?.instructor || {}),
            ...(row?.instructor || {}),
         };
      }

      if (merged?.student || row?.student) {
         merged.student = {
            ...(merged?.student || {}),
            ...(row?.student || {}),
         };
      }

      if (merged?.user || row?.user) {
         merged.user = {
            ...(merged?.user || {}),
            ...(row?.user || {}),
         };
      }
   }

   return merged;
}

function upsertReservationInLoadedStudentCache(state, row) {
   if (!row || typeof row !== "object") return;

   const id = reservationEntityIdStr(row);
   if (!id) return;
   if (isReservationSoftDeleted(row)) return;

   const sidRaw = getStudentId(row);
   if (sidRaw == null) return;
   const sid = String(sidRaw).trim();
   if (!sid) return;

   const buckets = state?.byStudent;
   if (!buckets || typeof buckets !== "object") return;
   if (!Object.prototype.hasOwnProperty.call(buckets, sid)) return;

   const arr = Array.isArray(buckets[sid]) ? buckets[sid] : [];
   const idx = arr.findIndex((r) => reservationEntityIdStr(r) === id);
   if (idx === -1) buckets[sid] = [row, ...arr];
   else arr[idx] = { ...arr[idx], ...row };
}

function clearLocalSyncFlags(row) {
   if (!row || typeof row !== "object") return row;

   const next = { ...row };
   delete next._optimistic;
   delete next._optimisticPending;
   delete next._localEchoPending;
   delete next._localEchoUntil;
   return next;
}

function getReservationUpdatedAtMs(row) {
   if (!row || typeof row !== "object") return 0;

   const raw =
      row.updatedAt ??
      row.updated_at ??
      row.modifiedAt ??
      row.modified_at ??
      row.createdAt ??
      row.created_at ??
      row.reservation?.updatedAt ??
      row.reservation?.updated_at ??
      row.reservation?.modifiedAt ??
      row.reservation?.modified_at ??
      row.reservation?.createdAt ??
      row.reservation?.created_at ??
      null;

   const ms = raw ? new Date(raw).getTime() : 0;
   return Number.isFinite(ms) ? ms : 0;
}

function hasActiveLocalSyncProtection(row, now = Date.now()) {
   if (!row || typeof row !== "object") return false;
   if (row._optimistic || row._optimisticPending) return true;
   if (!row._localEchoPending) return false;

   const until = Number(row._localEchoUntil);
   if (!Number.isFinite(until)) return true;
   return until > now;
}

/** Lista are rânduri al căror rezultat la aplicarea lunii depinde de timp (optimiste, ecou local, șterse recent)? */
export function hasLocalReservationProtection(reservationsState, now = Date.now()) {
   const deleted = reservationsState?.recentlyDeleted;
   if (deleted && typeof deleted === "object") {
      for (const key of Object.keys(deleted)) {
         if (Number(deleted[key]) > now) return true;
      }
   }
   const list = reservationsState?.list;
   if (Array.isArray(list)) {
      for (const row of list) {
         if (hasActiveLocalSyncProtection(row, now)) return true;
      }
   }
   return false;
}

function markReservationLocalEcho(row, ttlMs = LOCAL_ECHO_TTL_MS) {
   if (!row || typeof row !== "object") return row;
   return {
      ...clearLocalSyncFlags(row),
      _localEchoPending: true,
      _localEchoUntil: Date.now() + ttlMs,
   };
}

function mergeMonthSnapshotWithProtectedRow(
   prevRow,
   nextRow,
   now = Date.now(),
) {
   if (!prevRow || typeof prevRow !== "object") return nextRow;
   if (!nextRow || typeof nextRow !== "object") return prevRow;

   const prevUpdatedMs = getReservationUpdatedAtMs(prevRow);
   const nextUpdatedMs = getReservationUpdatedAtMs(nextRow);
   const snapshotClearlyNewer =
      nextUpdatedMs > 0 && nextUpdatedMs > prevUpdatedMs;

   if (snapshotClearlyNewer) {
      return clearLocalSyncFlags(
         reconcileSubstitutionEmbeds(
            mergeReservationRows(prevRow, nextRow),
            nextRow,
         ),
      );
   }

   const merged = mergeReservationRows(nextRow, prevRow);
   const reconciled = reconcileSubstitutionEmbeds(merged, nextRow);
   return hasActiveLocalSyncProtection(prevRow, now)
      ? reconciled
      : clearLocalSyncFlags(reconciled);
}

// Aplică un snapshot complet de la server (ex: refetch pe lună / all) peste
// lista curentă, PĂSTRÂND rândurile optimiste / local-echo încă neconfirmate.
// Fără asta, un refetch care ajunge cât timp o creare/mutare e „în zbor"
// șterge modificările abia făcute (simptom: „nu modifică tot până la urmă").
function applyServerSnapshotPreservingProtected(
   prevList,
   incomingItems,
   now = Date.now(),
   recentlyDeleted = null,
) {
   const normalized = normalizeIncomingReservations(incomingItems);
   // Elimină rândurile pe care le-am șters local recent, ca să nu fie reînviate
   // de un snapshot întârziat de la server.
   const nextList = recentlyDeleted
      ? normalized.filter(
           (row) =>
              !isRecentlyDeletedId(
                 recentlyDeleted,
                 reservationEntityIdStr(row),
                 now,
              ),
        )
      : normalized;
   const nextIndexById = new Map();

   for (let i = 0; i < nextList.length; i++) {
      const id = reservationEntityIdStr(nextList[i]);
      if (!id) continue;
      nextIndexById.set(id, i);
   }

   for (const prevRow of Array.isArray(prevList) ? prevList : []) {
      if (!prevRow) continue;
      if (!hasActiveLocalSyncProtection(prevRow, now)) continue;

      const id = reservationEntityIdStr(prevRow);
      if (!id) continue;
      if (isRecentlyDeletedId(recentlyDeleted, id, now)) continue;
      const existingIndex = nextIndexById.get(id);

      if (existingIndex == null) {
         nextIndexById.set(id, nextList.length);
         nextList.push(prevRow);
         continue;
      }

      nextList[existingIndex] = mergeMonthSnapshotWithProtectedRow(
         prevRow,
         nextList[existingIndex],
         now,
      );
   }

   return nextList;
}

function mergeIncomingReservationWithCurrentRow(
   prevRow,
   nextRow,
   now = Date.now(),
) {
   if (!prevRow || typeof prevRow !== "object") return nextRow;
   if (!nextRow || typeof nextRow !== "object") return prevRow;

   const prevUpdatedMs = getReservationUpdatedAtMs(prevRow);
   const nextUpdatedMs = getReservationUpdatedAtMs(nextRow);
   const incomingClearlyNewer =
      nextUpdatedMs > 0 && nextUpdatedMs > prevUpdatedMs;

   if (incomingClearlyNewer) {
      return clearLocalSyncFlags(
         reconcileSubstitutionEmbeds(
            mergeReservationRows(prevRow, nextRow),
            nextRow,
         ),
      );
   }

   const merged = mergeReservationRows(nextRow, prevRow);
   const reconciled = reconcileSubstitutionEmbeds(merged, nextRow);
   return hasActiveLocalSyncProtection(prevRow, now)
      ? reconciled
      : clearLocalSyncFlags(reconciled);
}

function mergeStudentItemsWithCurrentList(
   state,
   studentId,
   incomingItems,
   now = Date.now(),
) {
   const sid = normalizeReservationIdLike(studentId);
   const nextItems = Array.isArray(incomingItems) ? [...incomingItems] : [];
   if (!sid) return nextItems;

   const nextIndexById = new Map();
   for (let i = 0; i < nextItems.length; i++) {
      const id = reservationEntityIdStr(nextItems[i]);
      if (!id) continue;
      nextIndexById.set(id, i);
   }

   for (const prevRow of state.list || []) {
      if (!prevRow) continue;
      if (String(getStudentId(prevRow) ?? "").trim() !== sid) continue;

      const id = reservationEntityIdStr(prevRow);
      if (!id) continue;

      const nextIdx = nextIndexById.get(id);
      if (nextIdx == null) {
         if (!hasActiveLocalSyncProtection(prevRow, now)) continue;
         nextIndexById.set(id, nextItems.length);
         nextItems.push(prevRow);
         continue;
      }

      nextItems[nextIdx] = mergeIncomingReservationWithCurrentRow(
         prevRow,
         nextItems[nextIdx],
         now,
      );
   }

   return nextItems;
}

function indexBusyPayload(raw) {
   const arr = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.reservations)
        ? raw.reservations
        : Array.isArray(raw?.busy)
          ? raw.busy
          : [];

   const busyLookup = {};
   const busyIndex = {};
   const groupsSet = new Set();
   const instrSet = new Set();

   for (const r of arr) {
      const start = getStart(r);
      if (!start) continue;
      const iso = new Date(start).toISOString();

      const entry = {
         instructorsGroupId: getGroupId(r),
         instructorId: getInstructorId(r),
      };

      if (!busyIndex[iso]) busyIndex[iso] = [];
      busyIndex[iso].push(entry);
      busyLookup[iso] = true;

      if (entry.instructorsGroupId != null)
         groupsSet.add(String(entry.instructorsGroupId));
      if (entry.instructorId != null) instrSet.add(String(entry.instructorId));
   }

   return {
      busyLookup,
      busyIndex,
      availableGroups: Array.from(groupsSet),
      availableInstructors: Array.from(instrSet),
      raw: arr,
   };
}

// --- Slice ---
const reservationsSlice = createSlice({
   name: "reservations",
   initialState: {
      // calendar (global)
      list: [],
      loadingAll: false,
      errorAll: null,

      // compat
      loading: false,
      error: null,

      // popup student
      byStudent: {},
      loadingByStudent: {},
      errorByStudent: {},

      // busy
      busyLoading: false,
      busyError: null,
      busyLookup: {},
      busyIndex: {},
      availableGroups: [],
      availableInstructors: [],
      busyQuery: null,

      /* ───────────── meta pentru sync ───────────── */
      lastSyncedAt: null, // ultimul timestamp folosit la delta
      etag: null, // dacă serverul trimite ETag
      hydrated: false, // devine true după prima rehidratare / fetch OK

      // 🔥 diapazonul activ de rezervări (ex: luna curentă)
      activeRange: null, // { startDateFrom, startDateTo }
      monthSnapshot: {
         monthKey: "",
         items: [],
         range: null,
         appliedAt: null,
      },
      monthFetchRequestId: null, // protecție împotriva răspunsurilor vechi out-of-order
      deltaFetchRequestId: null,

      // piatră de mormânt locală: id -> expiresAt (ms). Blochează reînvierea
      // rândurilor șterse optimist de către un refetch/delta întârziat.
      recentlyDeleted: {},
   },
   reducers: {
      setReservationColorLocal: (state, action) => {
         const { id, color } = action.payload || {};
         const targetId = normalizeReservationIdLike(id);
         if (!targetId) return;
         const idx = state.list.findIndex(
            (r) => reservationEntityIdStr(r) === targetId,
         );
         if (idx !== -1) {
            const nextRow = { ...state.list[idx], color };
            state.list[idx] = nextRow;
            removeReservationFromLoadedStudentCaches(state, targetId);
            upsertReservationInLoadedStudentCache(state, nextRow);
         }
      },
      resetBusy(state) {
         state.busyLoading = false;
         state.busyError = null;
         state.busyLookup = {};
         state.busyIndex = {};
         state.availableGroups = [];
         state.availableInstructors = [];
         state.busyQuery = null;
      },
      clearStudentReservations(state, action) {
         const sid = String(action.payload);
         delete state.byStudent[sid];
         delete state.loadingByStudent[sid];
         delete state.errorByStudent[sid];
      },

      // 🔥 NEW: ștergere instant locală (optimistic)
      addReservationLocal(state, action) {
         const row = action.payload;
         if (!row) return;

         const id = reservationEntityIdStr(row);
         if (!id) return;
         // Un rând re-adăugat local nu mai e „șters".
         clearRecentlyDeletedId(state.recentlyDeleted, id);
         const idx = state.list.findIndex(
            (r) => reservationEntityIdStr(r) === id,
         );
         if (idx === -1) state.list.unshift(row);
         else state.list[idx] = { ...state.list[idx], ...row };

         removeReservationFromLoadedStudentCaches(state, id);
         upsertReservationInLoadedStudentCache(state, row);
      },
      removeReservationLocal(state, action) {
         const id = normalizeReservationIdLike(action.payload);
         if (!id) return;
         if (!state.recentlyDeleted || typeof state.recentlyDeleted !== "object") {
            state.recentlyDeleted = {};
         }
         const now = Date.now();
         pruneRecentlyDeleted(state.recentlyDeleted, now);
         const existingRow = state.list.find(
            (r) => reservationEntityIdStr(r) === id,
         );
         const hadLocalEcho = hasActiveLocalSyncProtection(existingRow, now);
         markRecentlyDeletedId(state.recentlyDeleted, id, now, {
            extended: hadLocalEcho,
         });
         const before = Array.isArray(state.list) ? state.list.length : 0;
         if (!before) return;
         state.list = state.list.filter(
            (r) => reservationEntityIdStr(r) !== id,
         );
         if (state.list.length === before) return;
         removeReservationFromLoadedStudentCaches(state, id);
      },
      patchReservationLocal(state, action) {
         const { id, changes } = action.payload || {};
         if (!id || !changes) return;

         const targetId = normalizeReservationIdLike(id);
         if (!targetId) return;
         const row = state.list.find(
            (r) => reservationEntityIdStr(r) === targetId,
         );
         if (!row) return;

         Object.assign(row, changes);
         // Dacă rândul optimist a fost re-legat la un id real (confirmare),
         // acel id nu mai trebuie considerat „șters".
         const nextId = reservationEntityIdStr(row);
         clearRecentlyDeletedId(state.recentlyDeleted, nextId);
         removeReservationFromLoadedStudentCaches(state, targetId);
         upsertReservationInLoadedStudentCache(state, row);
      },
      // NEW: poți seta manual range-ul activ dacă vrei
      setActiveRange(state, action) {
         state.activeRange = action.payload || null;
      },
      setReservationsFromMonthQuery(state, action) {
         const { items, range } = action.payload || {};
         const now = Date.now();
         pruneRecentlyDeleted(state.recentlyDeleted, now);

         state.monthSnapshot = buildMonthSnapshotFromQueryPayload(action.payload);
         state.list = applyServerSnapshotPreservingProtected(
            state.list,
            items,
            now,
            state.recentlyDeleted,
         );
         state.loadingAll = false;
         state.loading = false;
         state.errorAll = null;
         state.error = null;
         state.lastSyncedAt = resolveLastSyncedAt(action.payload);
         state.hydrated = true;
         if (range) state.activeRange = range;
      },
   },
   extraReducers: (builder) => {
      /* ───────────── DELTA ───────────── */
      builder
         .addCase(fetchReservationsDelta.pending, (s, a) => {
            s.deltaFetchRequestId = a.meta.requestId || null;
         })
         .addCase(fetchReservationsDelta.fulfilled, (s, a) => {
            if (
               s.deltaFetchRequestId &&
               s.deltaFetchRequestId !== a.meta.requestId
            ) {
               return;
            }
            s.deltaFetchRequestId = null;

            const {
               items = [],
               deleted = [],
               etag = null,
               now,
            } = a.payload || {};
            const nowDelta = Date.now();
            // upsert local
            if (Array.isArray(items) && items.length) {
               pruneRecentlyDeleted(s.recentlyDeleted, nowDelta);
               const byId = new Map();
               for (const row of s.list) {
                  const id = reservationEntityIdStr(row);
                  if (!id) continue;
                  byId.set(id, row);
               }

               for (const it of items) {
                  const id = reservationEntityIdStr(it);
                  if (!id) continue;
                  const prev = byId.get(id);
                  // Nu reînvia un rând șters local recent (mutare/ștergere în
                  // zbor) printr-un delta întârziat de la server.
                  if (!prev && isRecentlyDeletedId(s.recentlyDeleted, id, nowDelta)) {
                     continue;
                  }
                  if (prev) {
                     byId.set(
                        id,
                        mergeIncomingReservationWithCurrentRow(prev, it, nowDelta),
                     );
                  } else {
                     byId.set(id, it);
                  }
               }
               s.list = Array.from(byId.values());
            }
            // tombstone (ștergeri) — nu șterge rânduri cu protecție locală activă
            if (Array.isArray(deleted) && deleted.length) {
               const rm = new Set(
                  deleted
                     .map((d) => normalizeReservationIdLike(d))
                     .filter(Boolean),
               );
               s.list = s.list.filter((r) => {
                  const id = reservationEntityIdStr(r);
                  if (!rm.has(id)) return true;
                  if (isRecentlyDeletedId(s.recentlyDeleted, id, nowDelta)) {
                     return false;
                  }
                  if (hasActiveLocalSyncProtection(r, nowDelta)) return true;
                  return false;
               });
            }
            s.lastSyncedAt = now || s.lastSyncedAt || new Date().toISOString();
            if (etag) s.etag = etag;
            s.hydrated = true;
         })
         .addCase(fetchReservationsDelta.rejected, (s, a) => {
            if (
               s.deltaFetchRequestId &&
               s.deltaFetchRequestId !== a.meta.requestId
            ) {
               return;
            }
            s.deltaFetchRequestId = null;
         });

      // ===== getReservations =====
      builder
         .addCase(fetchReservations.pending, (s) => {
            s.loadingAll = true;
            s.loading = true;
            s.errorAll = null;
            s.error = null;
         })
         .addCase(fetchReservations.fulfilled, (s, a) => {
            s.loadingAll = false;
            s.loading = false;
            s.list = applyServerSnapshotPreservingProtected(s.list, a.payload);
            s.lastSyncedAt = resolveLastSyncedAt(
               Array.isArray(a.payload) ? { items: a.payload } : a.payload,
            );
            s.hydrated = true;
         })
         .addCase(fetchReservations.rejected, (s, a) => {
            s.loadingAll = false;
            s.loading = false;
            s.errorAll = a.payload;
            s.error = a.payload;
         });

      // ===== ALL (calendar) =====
      builder
         .addCase(fetchAllReservations.pending, (s) => {
            s.loadingAll = true;
            s.loading = true;
            s.errorAll = null;
            s.error = null;
         })
         .addCase(fetchAllReservations.fulfilled, (s, a) => {
            s.loadingAll = false;
            s.loading = false;
            s.list = applyServerSnapshotPreservingProtected(s.list, a.payload);
            s.lastSyncedAt = resolveLastSyncedAt(
               Array.isArray(a.payload) ? { items: a.payload } : a.payload,
            );
            s.hydrated = true;
         })
         .addCase(fetchAllReservations.rejected, (s, a) => {
            s.loadingAll = false;
            s.loading = false;
            s.errorAll = a.payload;
            s.error = a.payload;
         })

         // ===== per-student (popup details) =====
         .addCase(fetchUserReservations.pending, (s, a) => {
            const sid = String(a.meta.arg ?? "");
            s.loading = true;
            s.error = null;
            if (sid) {
               s.loadingByStudent[sid] = true;
               s.errorByStudent[sid] = null;
            }
         })
         .addCase(fetchUserReservations.fulfilled, (s, a) => {
            const sid = String(a.meta.arg ?? "");
            const items = normalizeItemsFromResponse(a.payload);
            const now = Date.now();
            const mergedItems = sid
               ? mergeStudentItemsWithCurrentList(s, sid, items, now)
               : items;

            s.loading = false;
            s.error = null;

            if (sid) {
               s.byStudent[sid] = mergedItems;
               s.loadingByStudent[sid] = false;
               s.errorByStudent[sid] = null;
            }

            if (!sid) {
               s.list = mergedItems.slice();
            } else {
               const incomingById = new Map();
               for (const row of mergedItems) {
                  const id = reservationEntityIdStr(row);
                  if (!id) continue;
                  incomingById.set(id, row);
               }

               const matchedIds = new Set();
               for (let idx = s.list.length - 1; idx >= 0; idx -= 1) {
                  const current = s.list[idx];
                  if (String(getStudentId(current) ?? "") !== sid) continue;

                  const currentId = reservationEntityIdStr(current);
                  if (!currentId || !incomingById.has(currentId)) {
                     s.list.splice(idx, 1);
                     continue;
                  }

                  const incoming = incomingById.get(currentId);
                  matchedIds.add(currentId);
                  const merged = mergeIncomingReservationWithCurrentRow(
                     current,
                     incoming,
                     now,
                  );
                  if (merged !== current) {
                     s.list[idx] = merged;
                  }
               }

               for (const row of mergedItems) {
                  const id = reservationEntityIdStr(row);
                  if (id && matchedIds.has(id)) continue;
                  s.list.unshift(row);
               }
            }
            s.lastSyncedAt = resolveLastSyncedAt(a.payload);
            s.hydrated = true;
         })
         .addCase(fetchUserReservations.rejected, (s, a) => {
            const sid = String(a.meta.arg ?? "");
            const message =
               a.payload || a.error?.message || "Failed to load reservations";

            s.loading = false;
            s.error = message;

            if (sid) {
               s.loadingByStudent[sid] = false;
               s.errorByStudent[sid] = message;
            }
         });

      // ===== add / update / delete =====
      builder
         .addCase(addReservation.pending, (s) => {
            s.loadingAll = true;
            s.loading = true;
            s.errorAll = null;
            s.error = null;
         })
         .addCase(addReservation.fulfilled, (s, a) => {
            s.loadingAll = false;
            s.loading = false;
            const payload = a.payload;
            if (Array.isArray(payload)) {
               const nextRows = normalizeIncomingReservations(payload);
               if (nextRows.length) {
                  s.list.push(...nextRows);
                  for (const row of nextRows) {
                     const id = reservationEntityIdStr(row);
                     if (!id) continue;
                     removeReservationFromLoadedStudentCaches(s, id);
                     upsertReservationInLoadedStudentCache(s, row);
                  }
               }
            } else if (!isReservationSoftDeleted(payload)) {
               s.list.push(payload);
               const id = reservationEntityIdStr(payload);
               if (id) {
                  removeReservationFromLoadedStudentCaches(s, id);
                  upsertReservationInLoadedStudentCache(s, payload);
               }
            }
         })
         .addCase(addReservation.rejected, (s, a) => {
            s.loadingAll = false;
            s.loading = false;
            s.errorAll = a.payload;
            s.error = a.payload;
         })
         .addCase(updateReservation.pending, (s) => {
            s.loadingAll = true;
            s.loading = true;
            s.errorAll = null;
            s.error = null;
         })
         .addCase(updateReservation.fulfilled, (s, a) => {
            s.loadingAll = false;
            s.loading = false;
            const updated = a.payload;
            const id = normalizeReservationIdLike(
               updated?.id ??
                  updated?.reservationId ??
                  updated?.reservation_id ??
                  a.meta.arg?.id,
            );
            if (!id) return;
            let mergedRow = null;
            const idx = s.list.findIndex(
               (r) => reservationEntityIdStr(r) === id,
            );
            const cachedRow = findReservationInLoadedStudentCaches(s, id);
            if (idx !== -1) {
               const nextRow = markReservationLocalEcho(
                  mergeReservationRows(cachedRow, s.list[idx], updated),
               );
               if (isReservationSoftDeleted(nextRow)) s.list.splice(idx, 1);
               else {
                  s.list[idx] = nextRow;
                  mergedRow = nextRow;
               }
            } else if (!isReservationSoftDeleted(updated)) {
               mergedRow = markReservationLocalEcho(
                  mergeReservationRows(cachedRow, updated),
               );
               s.list.unshift(mergedRow);
            }

            removeReservationFromLoadedStudentCaches(s, id);
            if (mergedRow) upsertReservationInLoadedStudentCache(s, mergedRow);
         })
         .addCase(updateReservation.rejected, (s, a) => {
            s.loadingAll = false;
            s.loading = false;
            s.errorAll = a.payload;
            s.error = a.payload;
         })
         .addCase(updateReservationColor.fulfilled, (s, a) => {
            const updated = a.payload;
            const id = normalizeReservationIdLike(
               updated?.id ??
                  updated?.reservationId ??
                  updated?.reservation_id ??
                  a.meta.arg?.id,
            );
            if (!id) return;
            let mergedRow = null;
            const idx = s.list.findIndex(
               (r) => reservationEntityIdStr(r) === id,
            );
            const cachedRow = findReservationInLoadedStudentCaches(s, id);
            if (idx !== -1) {
               const nextRow = markReservationLocalEcho(
                  mergeReservationRows(cachedRow, s.list[idx], updated),
               );
               if (isReservationSoftDeleted(nextRow)) s.list.splice(idx, 1);
               else {
                  s.list[idx] = nextRow;
                  mergedRow = nextRow;
               }
            } else if (!isReservationSoftDeleted(updated)) {
               mergedRow = markReservationLocalEcho(
                  mergeReservationRows(cachedRow, updated),
               );
               s.list.unshift(mergedRow);
            }

            removeReservationFromLoadedStudentCaches(s, id);
            if (mergedRow) upsertReservationInLoadedStudentCache(s, mergedRow);
         })
         .addCase(removeReservation.fulfilled, (s, a) => {
            const id = normalizeReservationIdLike(a.payload);
            if (!id) return;
            const now = Date.now();
            pruneRecentlyDeleted(s.recentlyDeleted, now);
            markRecentlyDeletedId(s.recentlyDeleted, id, now);
            s.list = s.list.filter((r) => reservationEntityIdStr(r) !== id);
            removeReservationFromLoadedStudentCaches(s, id);
         });

      // ===== busy =====
      builder
         .addCase(fetchBusy.pending, (s) => {
            s.busyLoading = true;
            s.busyError = null;
         })
         .addCase(fetchBusy.fulfilled, (s, a) => {
            s.busyLoading = false;
            s.busyQuery = a.payload.query;
            const parsed = indexBusyPayload(a.payload.data);
            s.busyLookup = parsed.busyLookup;
            s.busyIndex = parsed.busyIndex;
            s.availableGroups = parsed.availableGroups;
            s.availableInstructors = parsed.availableInstructors;
         })
         .addCase(fetchBusy.rejected, (s, a) => {
            s.busyLoading = false;
            s.busyError = a.payload;
            s.busyLookup = {};
            s.busyIndex = {};
            s.availableGroups = [];
            s.availableInstructors = [];
         });

      /* ───────────── NEW: fetchReservationsFiltered / fetchReservationsForMonth ───────────── */
      builder
         .addCase(fetchReservationsFiltered.pending, (s) => {
            s.loadingAll = true;
            s.loading = true;
            s.errorAll = null;
            s.error = null;
         })
         .addCase(fetchReservationsFiltered.fulfilled, (s, a) => {
            s.loadingAll = false;
            s.loading = false;
            const { items, filters } = a.payload || {};
            s.list = applyServerSnapshotPreservingProtected(s.list, items);
            s.lastSyncedAt = resolveLastSyncedAt(a.payload);
            s.hydrated = true;

            if (filters?.startDateFrom || filters?.startDateTo) {
               s.activeRange = {
                  startDateFrom: filters.startDateFrom || null,
                  startDateTo: filters.startDateTo || null,
               };
            }
         })
         .addCase(fetchReservationsFiltered.rejected, (s, a) => {
            s.loadingAll = false;
            s.loading = false;
            s.errorAll = a.payload;
            s.error = a.payload;
         })
         .addCase(fetchReservationsForMonth.pending, (s, a) => {
            s.loadingAll = true;
            s.loading = true;
            s.errorAll = null;
            s.error = null;
            s.monthFetchRequestId = a.meta.requestId || null;
         })
         .addCase(fetchReservationsForMonth.fulfilled, (s, a) => {
            if (
               s.monthFetchRequestId &&
               s.monthFetchRequestId !== a.meta.requestId
            ) {
               return;
            }
            s.loadingAll = false;
            s.loading = false;
            const { items, range } = a.payload || {};
            s.list = applyServerSnapshotPreservingProtected(s.list, items);
            s.lastSyncedAt = resolveLastSyncedAt(a.payload);
            s.hydrated = true;
            s.monthFetchRequestId = null;
            if (range) s.activeRange = range;
         })
         .addCase(fetchReservationsForMonth.rejected, (s, a) => {
            if (
               s.monthFetchRequestId &&
               s.monthFetchRequestId !== a.meta.requestId
            ) {
               return;
            }
            s.loadingAll = false;
            s.loading = false;
            s.errorAll = a.payload;
            s.error = a.payload;
            s.monthFetchRequestId = null;
         });

      /* ───────────── state updates din maybeRefreshReservations ───────────── */
      builder
         .addCase(maybeRefreshReservations.fulfilled, (s, a) => {
            const { refreshed, etag, serverTime } = a.payload || {};
            if (etag) s.etag = etag;
            if (!refreshed && serverTime) s.lastSyncedAt = serverTime;
            if (s.list?.length) s.hydrated = true;
         })
         .addCase(maybeRefreshReservations.rejected, (s) => {
            // nimic, evităm să stricăm starea existentă
         });
   },
});

export const {
   setReservationColorLocal,
   resetBusy,
   clearStudentReservations,
   setActiveRange,
   setReservationsFromMonthQuery,
   addReservationLocal,
   removeReservationLocal,
   patchReservationLocal,
} = reservationsSlice.actions;

export default reservationsSlice.reducer;
